import {
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  Download,
  EyeOff,
  Filter,
  Pause,
  Play,
  Radio,
  WifiOff,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { cn } from '~/utils/cn';
import { triggerBrowserDownload } from '~/utils/download-file';

import type { HistoryStreamStatus } from '../../hooks/use-workflow-history-stream';
import type { HistoryEvent, HistoryEventCategory } from '../../types/workflow-inspection.types';
import { buildHistoryTimeline } from '../../utils/history-spans';

import { EventDetailDrawer } from './event-detail-drawer';
import { EventTable, type EventTableView } from './event-table';
import { EventTimeline } from './event-timeline';

const CATEGORIES: HistoryEventCategory[] = [
  'workflow',
  'workflowTask',
  'activity',
  'timer',
  'marker',
  'signal',
  'childWorkflow',
  'update',
  'other',
];

interface Props {
  workflowId: string;
  runId: string | null;
  /** Ascending, masked, de-duped events. */
  events: HistoryEvent[];
  streamStatus: HistoryStreamStatus;
  frozen: boolean;
  onFrozenChange: (frozen: boolean) => void;
  bufferedCount: number;
}

/**
 * Temporal-style Event History: toolbar (order, hide workflow tasks, category
 * filter, Freeze while live, Download), the span timeline, and the
 * All/Compact/JSON table, with a drawer for any clicked event. Ordering and
 * filtering are client-side over the loaded history — the server only offers
 * `all|close`. Download is built from the masked events, so nothing unmasked
 * leaves the page.
 */
export function EventHistorySection({
  workflowId,
  runId,
  events,
  streamStatus,
  frozen,
  onFrozenChange,
  bufferedCount,
}: Props) {
  const { t } = useTranslation('workflow-inspection');
  const [descending, setDescending] = useState(true);
  const [hideWorkflowTasks, setHideWorkflowTasks] = useState(false);
  const [categories, setCategories] = useState<HistoryEventCategory[]>([]);
  const [view, setView] = useState<EventTableView>('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const timeline = buildHistoryTimeline(events);
  const filtered = events.filter((e) => {
    if (hideWorkflowTasks && e.category === 'workflowTask') return false;
    return categories.length === 0 || categories.includes(e.category as HistoryEventCategory);
  });
  const ordered = descending ? [...filtered].reverse() : filtered;
  const orderedSpans = descending ? [...timeline.spans].reverse() : timeline.spans;
  const selected =
    selectedId === null ? null : (events.find((e) => e.eventId === selectedId) ?? null);
  const isLive =
    streamStatus === 'live' || streamStatus === 'degraded' || streamStatus === 'polling';

  function toggleCategory(category: HistoryEventCategory) {
    setCategories(
      categories.includes(category)
        ? categories.filter((c) => c !== category)
        : [...categories, category],
    );
  }

  function download() {
    const body = JSON.stringify({ workflowId, runId, events }, null, 2);
    triggerBrowserDownload(
      new Blob([body], { type: 'application/json' }),
      `${workflowId}${runId ? `_${runId}` : ''}.history.json`,
    );
  }

  return (
    <section className="space-y-3" data-testid="workflow-inspector-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-foreground text-base font-semibold">{t('history.title')}</h3>
          <StreamBadge status={streamStatus} />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="cursor-pointer gap-1.5"
            onClick={() => {
              setDescending(!descending);
            }}
            data-testid="workflow-inspector-history-sort-button"
          >
            {descending ? (
              <ArrowDownWideNarrow aria-hidden="true" className="size-4" />
            ) : (
              <ArrowUpNarrowWide aria-hidden="true" className="size-4" />
            )}
            {descending ? t('history.toolbar.descending') : t('history.toolbar.ascending')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn('cursor-pointer gap-1.5', hideWorkflowTasks && 'bg-accent')}
            aria-pressed={hideWorkflowTasks}
            data-testid="workflow-inspector-history-hide-workflow-tasks-toggle"
            onClick={() => {
              setHideWorkflowTasks(!hideWorkflowTasks);
            }}
          >
            <EyeOff aria-hidden="true" className="size-4" />
            {hideWorkflowTasks
              ? t('history.toolbar.showWorkflowTasks')
              : t('history.toolbar.hideWorkflowTasks')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="cursor-pointer gap-1.5"
                data-testid="workflow-inspector-history-filter-button"
              >
                <Filter aria-hidden="true" className="size-4" />
                {categories.length > 0
                  ? t('history.toolbar.filterCount', { count: categories.length })
                  : t('history.toolbar.filter')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="workflow-inspector-history-filter-menu">
              {CATEGORIES.map((c) => (
                <DropdownMenuCheckboxItem
                  key={c}
                  className="cursor-pointer"
                  data-testid={`workflow-inspector-history-filter-${c}-checkbox`}
                  checked={categories.includes(c)}
                  onCheckedChange={() => {
                    toggleCategory(c);
                  }}
                  onSelect={(e) => {
                    e.preventDefault();
                  }}
                >
                  {t(`history.category.${c}`)}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer"
                disabled={categories.length === 0}
                data-testid="workflow-inspector-history-filter-clear-item"
                onSelect={() => {
                  setCategories([]);
                }}
              >
                {t('history.toolbar.clearFilter')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {isLive && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn('cursor-pointer gap-1.5', frozen && 'bg-accent')}
              aria-pressed={frozen}
              data-testid="workflow-inspector-history-freeze-toggle"
              onClick={() => {
                onFrozenChange(!frozen);
              }}
            >
              {frozen ? (
                <Play aria-hidden="true" className="size-4" />
              ) : (
                <Pause aria-hidden="true" className="size-4" />
              )}
              {frozen
                ? bufferedCount > 0
                  ? t('history.toolbar.unfreezeWithCount', { count: bufferedCount })
                  : t('history.toolbar.unfreeze')
                : t('history.toolbar.freeze')}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="cursor-pointer gap-1.5"
            onClick={download}
            data-testid="workflow-inspector-history-download-button"
          >
            <Download aria-hidden="true" className="size-4" />
            {t('history.toolbar.download')}
          </Button>
        </div>
      </div>

      {(streamStatus === 'degraded' || streamStatus === 'polling') && (
        <p
          className="bg-warning/10 text-foreground flex items-center gap-2 rounded-md px-3 py-2 text-xs"
          data-testid="workflow-inspector-history-stream-degraded-banner"
        >
          <WifiOff aria-hidden="true" className="size-3.5 shrink-0" />
          {t(`history.stream.${streamStatus}`)}
        </p>
      )}

      {events.length === 0 ? (
        <p
          className="text-muted-foreground rounded-lg border p-6 text-center text-sm"
          data-testid="workflow-inspector-history-empty"
        >
          {t('history.empty')}
        </p>
      ) : (
        <>
          <EventTimeline timeline={timeline} onSelectEvent={setSelectedId} />
          <EventTable
            events={ordered}
            spans={orderedSpans}
            view={view}
            onViewChange={setView}
            onSelectEvent={setSelectedId}
          />
        </>
      )}

      <EventDetailDrawer
        event={selected}
        onClose={() => {
          setSelectedId(null);
        }}
      />
    </section>
  );
}

/** Small live/connecting/closed marker beside the section title. */
function StreamBadge({ status }: { status: HistoryStreamStatus }) {
  const { t } = useTranslation('workflow-inspection');
  if (status === 'idle') return null;
  const label =
    status === 'connecting'
      ? t('history.stream.connecting')
      : status === 'closed'
        ? t('history.stream.closed')
        : t('history.stream.live');
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        status === 'closed' ? 'bg-muted text-muted-foreground' : 'bg-success/10 text-success',
      )}
      data-testid="workflow-inspector-history-stream-badge"
      data-status={status}
    >
      <Radio
        aria-hidden="true"
        className={cn('size-3', status !== 'closed' && 'animate-pulse motion-reduce:animate-none')}
      />
      {label}
    </span>
  );
}
